/**
 * `SiteCard` — ported from `Foyer_Dashboard_CURRENT.jsx` line 903.
 *
 * Phase 1 changes:
 *   - Stats are not yet in the typed `Site` shape (calls/revenue/disputes
 *     live in Phase 2 usage aggregates). For pending_verification we
 *     render the "needs verification" panel — copy points the merchant
 *     at the install + verify flow (snippet, .well-known, or Shopify
 *     OAuth proof per ADR-0043); the legacy CNAME / verification-file
 *     remedy was retired and the card no longer mentions it. For active
 *     we render placeholder zeros so the visual rhythm survives.
 *   - "Remove site" is now ENABLED (founder cleanup of test-site clutter).
 *     The action soft-deletes server-side — a hard delete would 500 on
 *     any site with audit records (the chain FKs are `ON DELETE RESTRICT`).
 *     Click opens the `ConfirmDialog` atom with `tone='coral'` (the
 *     destructive-action color); confirming calls
 *     `api.sites.delete(site_id)` and notifies the parent via
 *     `onDeleted(site_id)` so the SitesList can drop the row from
 *     its local array (or `refresh()` to converge with the server).
 *   - Three render states (ADR-0025 §5 install signal):
 *       * `pending_verification` — ownership not proven yet (the embed
 *                                  hasn't sent a proof-bearing ping AND
 *                                  no OAuth-rail proof has landed);
 *                                  "Continue setup" routes back into the
 *                                  install + verify step. (ADR-0043
 *                                  retired the CNAME / verification-file
 *                                  remedy; the active remedies are
 *                                  snippet-presence, `.well-known`, and
 *                                  the Shopify OAuth proof.)
 *       * `awaiting_install`    — verified but `last_seen_at IS NULL`
 *                                 (no ping yet); "View install snippet".
 *       * `live`                — verified + at least one ping has
 *                                 arrived; stats panel as before.
 *     The middle state was previously invisible: an active site with
 *     no traffic rendered as fully live, so merchants who forgot to
 *     paste the embed had no signal in the dashboard.
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ChevronRight,
  ExternalLink,
  MoreHorizontal,
  Settings as SettingsIcon,
  Trash2,
} from 'lucide-react';
import { useContext, useEffect, useState } from 'react';

import type { Site, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { SiteCtx, useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import {
  siteMode,
  siteModeDetailLabel,
  siteModeLabel,
  siteState,
  siteStaleSignal,
  type ShopifyEmbedInstallStatus,
} from '../../lib/site-state.js';
import { Card, ConfirmDialog, Modal, StatusDot } from '../../components/atoms/index.js';
import { installUpdateCopy } from '../../lib/install-update-copy.js';
import { ShopifyManualInstallPanel } from './ShopifyManualInstallPanel.js';
import { SiteStat } from './SiteStat.js';

// The stale-signal payload (>24h with no beacon) is the only freshness
// tier promoted to first-class card real estate. Green (<60min) and
// amber (60min-24h) are deliberately not surfaced here: doing so is
// alarm-fatigue bait on low-traffic sites and reads as redundancy on
// healthy ones. Out-of-stack alerting on shorter windows lives in
// `.github/workflows/ingest-heartbeat.yml`. The threshold + relative
// label are single-sourced in `lib/site-state.ts` so this card and the
// Mandates empty-state agree.

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export interface SiteCardProps {
  site: Site;
  onClick: () => void;
  onSettings: () => void;
  /**
   * Triggered by the "View install snippet" CTA in the awaiting_install
   * state. Caller is expected to set `activeSite` and route to
   * `onboarding`; the Onboarding shell resumes at step 3 (Install).
   */
  onInstall: () => void;
  /**
   * Fired after a successful soft-delete. The parent (`SitesList`)
   * removes the site from its local array — mirroring how `onSettings`
   * is threaded — and clears `activeSite` if the deleted site was
   * active. The card itself never owns the site array; it just
   * surfaces the side-effect.
   */
  onDeleted: (site_id: SiteId) => void;
  /**
   * ADR-0057 §5 — deep-link target for the UNREGISTERED ORIGINS band's
   * "Review →" link. The band lands the merchant directly on this
   * site's Origins settings tab (allow-list management) instead of
   * dumping them on the generic Settings page. Optional so existing
   * test scaffolding that doesn't care about the band keeps mounting
   * the card without changes — when omitted, the band falls back to
   * `onSettings` (the pre-ADR-0057 behavior).
   */
  onReviewOrigins?: () => void;
  /**
   * Founder 2026-10-27 — the update band opens Settings -> Install on
   * this site's "Update available" callout. Falls back to `onSettings`.
   */
  onInstallUpdate?: () => void;
}

export function SiteCard({
  site,
  onClick,
  onSettings,
  onInstall,
  onDeleted,
  onReviewOrigins,
  onInstallUpdate,
}: SiteCardProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  // ADR-0056 — read the SiteCtx directly rather than `useSites()` so
  // existing tests that mount SiteCard outside a SiteProvider (the
  // delete-flow + autoinstall suites pass a stub api bag and skip the
  // provider) keep rendering. The `updateSite` call is best-effort
  // post-fetch convergence; null-safe behavior is fine when absent.
  const siteCtx = useContext(SiteCtx);
  const updateSite = siteCtx?.updateSite;
  const [menuOpen, setMenuOpen] = useState(false);
  // ADR-0056 — local state for the manual-install panel's "check now"
  // affordance. `checkingPing` flips while we re-fetch the site row;
  // `checkResultMessage` carries the post-roundtrip honest copy ("no
  // ping yet — Shopify CDN cache can be a few minutes; try again").
  const [checkingPing, setCheckingPing] = useState(false);
  const [checkResultMessage, setCheckResultMessage] = useState<
    string | undefined
  >(undefined);
  // ADR-0051 §5 — two-stage removal modal. Stage 1 ("intentOpen")
  // is the intent picker — the merchant chooses "Stop recording"
  // (soft-delete; Slice 1) or "Erase content" (crypto-shredding;
  // disabled until Slice 2). Stage 2 ("confirmOpen") is the
  // corrected soft-delete confirmation copy that matches what the
  // back end actually does (preserve the audit chain, route the
  // merchant to the deleted-site archive). The two-stage shape
  // surfaces both intents in the UI so a merchant who wants
  // erasure sees it labeled "available in a later release" rather
  // than discovering the missing capability only after pressing
  // a button that doesn't deliver it.
  const [intentOpen, setIntentOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // ADR-0053 §3.1 — Shopify-only post-OAuth ScriptTag-install outcome.
  // Fetched on mount when the site is verified-but-no-ping-yet so the
  // card can branch between auto-installed copy and the existing
  // paste-the-snippet / reconnect / retry copy. The fetch is a no-op
  // for sites that are still in pending_verification or already live.
  // The retrieval is RLS-scoped server-side; a missing connector row
  // (non-shopify site) resolves to no `embed_install_status` on the
  // response and the card falls through to the manual-paste copy.
  const [shopifyInstallStatus, setShopifyInstallStatus] =
    useState<ShopifyEmbedInstallStatus | undefined>(undefined);
  const [retryingInstall, setRetryingInstall] = useState(false);
  // ADR-0181 §1 — Shopify-only Storefront consent status. Fetched
  // alongside the install status; the re-authorize affordance renders
  // when the value is present and NOT 'granted'. Absent = not a
  // Shopify site (or the server has not fetched yet); the affordance
  // stays hidden until we know for certain.
  const [shopifyStorefrontConsentStatus, setShopifyStorefrontConsentStatus] =
    useState<'granted' | 'pending' | 'pending_retry' | 'denied' | undefined>(
      undefined,
    );
  const [retryingStorefrontMint, setRetryingStorefrontMint] = useState(false);
  // ADR-0057 — unregistered-origins count. Fetched on mount for live
  // sites (where flagged audit records are actually possible). A
  // positive count surfaces the warning band BELOW the header so the
  // merchant sees "your pixel fired from N unregistered origins" at a
  // glance. Tenant-scoped; cross-tenant resolves to 0 by RLS.
  const [unregisteredOriginsCount, setUnregisteredOriginsCount] = useState<
    number | undefined
  >(undefined);
  const baseState = siteState(site);
  const verifiedNoPing = baseState === 'awaiting_install';
  useEffect(() => {
    if (!verifiedNoPing) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const status = await api.sites.getConnectorStatus(
          site.site_id,
          'shopify',
        );
        if (cancelled) return;
        // The field is optional; an undefined value means the server
        // saw no shopify integration row OR the row lacks the column
        // (legacy data) — both resolve to "render manual-paste copy."
        setShopifyInstallStatus(status.embed_install_status);
        // ADR-0181 §1 — capture the Storefront consent status from the
        // same fetch (additive; existing branches ignore this field).
        setShopifyStorefrontConsentStatus(status.storefront_consent_status);
      } catch {
        // Swallow — the card falls back to the manual-paste copy.
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [api, site.site_id, verifiedNoPing]);

  const state = siteState(site, {
    ...(shopifyInstallStatus !== undefined
      ? { shopify_embed_install_status: shopifyInstallStatus }
      : {}),
  });
  const isLive = state === 'live';
  const staleSignal = isLive ? siteStaleSignal(site.last_activity_at) : null;

  // ADR-0181 §1 — for a LIVE Shopify site, fetch the connector status
  // so the re-authorize affordance can decide to render. Runs alongside
  // the existing awaiting-install fetch above (that one is scoped to
  // `verifiedNoPing`); the LIVE branch needs its own fetch to know
  // (a) whether the site is Shopify at all and (b) whether the
  // Storefront consent has been granted. Best-effort — a fetch failure
  // hides the affordance rather than surfacing a broken UI.
  useEffect(() => {
    if (!isLive) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const status = await api.sites.getConnectorStatus(
          site.site_id,
          'shopify',
        );
        if (cancelled) return;
        setShopifyStorefrontConsentStatus(status.storefront_consent_status);
      } catch {
        // Swallow — the affordance simply does not render.
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [api, site.site_id, isLive]);

  // ADR-0057 — fetch the per-site flagged-origins aggregate when the
  // site is live (the warning band only renders for live sites; the
  // fetch is a no-op for pending_verification / awaiting_install
  // sites where no audit records exist yet). Best-effort; a fetch
  // failure leaves `unregisteredOriginsCount` undefined and the band
  // simply does not render.
  useEffect(() => {
    if (!isLive) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const res = await api.sites.getUnregisteredOrigins(site.site_id);
        if (cancelled) return;
        setUnregisteredOriginsCount(res.total_records);
      } catch {
        // Swallow; the band simply does not render.
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [api, site.site_id, isLive]);

  // ADR-0056 — re-fetch the site row and reflect whether a first ping
  // has landed since the merchant pasted the manual snippet. The poll
  // ALREADY runs in the background on the dashboard's site-list refresh
  // interval; this is the merchant's explicit "I added it, check now"
  // affordance. Honest copy on a miss (Shopify CDN caches CSS/JS for a
  // few minutes; we don't pretend we can see what we can't).
  async function handleCheckInstalled(): Promise<void> {
    if (checkingPing) return;
    setCheckingPing(true);
    setCheckResultMessage(undefined);
    try {
      const fresh = await api.sites.get(site.site_id);
      if (fresh !== null) {
        updateSite?.(fresh.site_id, fresh);
        if (fresh.last_seen_at !== undefined && fresh.last_seen_at !== null) {
          // The card's outer state derivation re-runs on the updated
          // site row and transitions to the `live` panel — no extra
          // message needed here.
          setCheckResultMessage(undefined);
          return;
        }
      }
      setCheckResultMessage(
        "We haven't seen a ping yet. Shopify can cache CSS and JS for a"
        + ' few minutes — try again in a moment.',
      );
    } catch {
      setCheckResultMessage(
        "We couldn't check just now. Try again in a moment.",
      );
    } finally {
      setCheckingPing(false);
    }
  }

  async function handleRetryInstall(): Promise<void> {
    if (retryingInstall) return;
    setRetryingInstall(true);
    try {
      const result = await api.sites.retryShopifyEmbedInstall(site.site_id);
      setShopifyInstallStatus(result.embed_install_status);
      if (result.embed_install_status === 'installed') {
        toast({
          title: 'Embed installed',
          body: `Sill installed the embed on ${site.domain}.`,
          tone: 'mint',
        });
      } else if (result.embed_install_status === 'scope_denied') {
        toast({
          title: 'Reconnect required',
          body: 'Re-authorize Shopify to finish installing the embed.',
          tone: 'amber',
        });
      } else {
        // Neutral copy: `pending_retry` can be a Shopify-side failure
        // OR a server-side failure that happened before Shopify was
        // contacted (ADR-0055). Don't blame Shopify when we can't
        // distinguish the cause.
        toast({
          title: 'Install still pending',
          body: 'The embed install did not complete. Try again in a moment.',
          tone: 'coral',
        });
      }
    } catch (err) {
      let body = 'Please try again.';
      if (err instanceof ApiError) {
        if (err.kind === 'rate_limited') {
          body = 'Too many retries. Try again later.';
        } else if (err.kind === 'conflict') {
          body = 'Shopify is no longer connected. Reconnect to retry.';
        }
      }
      toast({
        title: 'Could not retry install',
        body,
        tone: 'coral',
      });
    } finally {
      setRetryingInstall(false);
    }
  }

  async function handleRetryStorefrontMint(): Promise<void> {
    // ADR-0181 §Handoff — dashboard-driven retry of the Shopify
    // Storefront-Access-Token mint. Runs against the same
    // `POST /v1/sites/:site_id/connector/shopify/mint-storefront-token`
    // endpoint the OAuth callback uses; typed errors surface a coral
    // toast so the merchant sees the actual blocker (rate limit, no
    // longer connected).
    if (retryingStorefrontMint) return;
    setRetryingStorefrontMint(true);
    try {
      const result = await api.sites.retryShopifyStorefrontTokenMint(
        site.site_id,
      );
      setShopifyStorefrontConsentStatus(result.storefront_consent_status);
      if (result.storefront_consent_status === 'granted') {
        toast({
          title: 'Recommendations unlocked',
          body:
            'Sill can now serve recommendation lookups by SKU on '
            + `${site.domain}.`,
          tone: 'mint',
        });
      } else {
        toast({
          title: 'Retry did not complete',
          body: 'Storefront access could not be enabled. Try again in a moment.',
          tone: 'coral',
        });
      }
    } catch (err) {
      let body = 'Please try again.';
      if (err instanceof ApiError) {
        if (err.kind === 'rate_limited') {
          body = 'Too many retries. Try again later.';
        } else if (err.kind === 'conflict') {
          body = 'Shopify is no longer connected. Reconnect to retry.';
        }
      }
      toast({
        title: 'Could not retry Storefront access',
        body,
        tone: 'coral',
      });
    } finally {
      setRetryingStorefrontMint(false);
    }
  }

  async function handleConfirmDelete(): Promise<void> {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.sites.delete(site.site_id);
      toast({
        title: 'Site removed',
        body: `${site.domain} no longer recording.`,
        tone: 'mint',
      });
      onDeleted(site.site_id);
    } catch (err) {
      // Typed-error mapping: 403 means the caller is a reviewer /
      // viewer (server gate); 404 means the site is already gone
      // (another tab won the race); anything else is a generic
      // failure. We surface a coral toast so the user knows the
      // action did not land and the row stays put.
      let body = 'Please try again.';
      if (err instanceof ApiError) {
        if (err.kind === 'forbidden') {
          body = 'You need an owner or admin role to remove a site.';
        } else if (err.kind === 'not_found') {
          body = 'This site was already removed.';
          // Best-effort: tell the parent so the row drops anyway.
          onDeleted(site.site_id);
        }
      }
      toast({
        title: 'Could not remove site',
        body,
        tone: 'coral',
      });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
    <Card className="overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <button
            type="button"
            onClick={onClick}
            className="flex-1 text-left min-w-0"
            aria-label={`Open ${site.domain}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <StatusDot color={isLive ? C.lime : C.amber} />
              <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                {state === 'pending_verification'
                  ? 'NOT YET VERIFIED'
                  : state === 'awaiting_install'
                    ? 'AWAITING TRAFFIC'
                    : state === 'awaiting_first_ping_shopify_autoinstall'
                      ? 'AWAITING FIRST AGENT VISIT'
                      // Founder 2026-10-20: derive the mode honestly from
                      // the server's status (rail + baseline policy seed)
                      // instead of the hard-coded 'DISCOVERY · LIVE'.
                      // Founder 2026-10-21: a Discovery site with an
                      // active payment-rail connector reads as "Stripe
                      // connected · not taking orders yet" instead of a
                      // bare "Discovery" (which falsely implies no rail).
                      : siteModeDetailLabel(site).toUpperCase()}
              </span>
            </div>
            <div
              style={{
                fontFamily: serifFont,
                fontSize: 22,
                color: C.text,
                letterSpacing: -0.5,
                lineHeight: 1.1,
                marginBottom: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>{site.name}</span>
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {site.domain}
            </div>
          </button>
          <div className="relative ml-2">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={`${site.domain} actions`}
              aria-expanded={menuOpen}
              className="w-7 h-7 flex items-center justify-center"
              style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
            >
              <MoreHorizontal size={13} color={C.muted} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-full right-0 mt-1 z-30"
                  style={{
                    width: 200,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 3,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.3)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onSettings();
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                    style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}
                  >
                    <SettingsIcon size={11} /> Site settings
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      window.open(`https://${site.domain}`, '_blank', 'noopener,noreferrer');
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                    style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}
                  >
                    <ExternalLink size={11} /> Visit site
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      // ADR-0051 §5 — open the two-stage intent picker
                      // first, not the confirmation dialog directly.
                      setIntentOpen(true);
                    }}
                    disabled={deleting}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                    style={{
                      borderTop: `1px solid ${C.borderSoft}`,
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.coralFg,
                      background: 'transparent',
                      cursor: deleting ? 'wait' : 'pointer',
                      opacity: deleting ? 0.6 : 1,
                    }}
                  >
                    <Trash2 size={11} /> Remove site
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Founder 2026-10-27 — replaces the unlabeled amber dot: says
            which install is behind and opens the steps in one click. */}
        {site.install_update !== undefined ? (() => {
          const copy = installUpdateCopy(site.install_update);
          return (
            <button
              type="button"
              data-testid="site-install-update-band"
              onClick={(e): void => {
                e.stopPropagation();
                if (onInstallUpdate !== undefined) onInstallUpdate();
                else onSettings();
              }}
              className="w-full p-3 mb-4 text-left"
              style={{
                background: C.amberDim,
                border: `1px solid rgba(255,182,39,0.3)`,
                borderRadius: 3,
                cursor: 'pointer',
              }}
              aria-label={`${copy.install} update ${copy.versions} for ${site.domain}. Open update steps`}
            >
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={12} color={C.amberFg} />
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.amberFg, letterSpacing: 1, fontWeight: 600 }}>
                  UPDATE AVAILABLE
                </span>
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.4 }}>
                <strong style={{ fontWeight: 600 }}>{copy.band}.</strong>{' '}
                <span style={{ color: C.muted }}>Update →</span>
              </div>
            </button>
          );
        })() : null}

        {state === 'live' ? (
          <>
            {/* ADR-0057 — warning band for unregistered-origin traffic.
               Renders ABOVE the stats grid so a merchant scanning the
               card sees the integrity signal before the headline
               numbers (which can otherwise look healthy while the
               recorded traffic is actually contaminated). Honest copy
               — "Sill recorded traffic from N origins that aren't
               registered" — not alarmist; the merchant decides what
               to do (allow-list, hide, investigate). */}
            {unregisteredOriginsCount !== undefined
              && unregisteredOriginsCount > 0 ? (
              <button
                type="button"
                onClick={(e): void => {
                  e.stopPropagation();
                  // ADR-0057 §5 — prefer the Origins deep-link when the
                  // parent threaded it; fall back to the generic
                  // settings handler for older callers / test
                  // scaffolding so the band stays clickable.
                  if (onReviewOrigins !== undefined) {
                    onReviewOrigins();
                  } else {
                    onSettings();
                  }
                }}
                className="w-full p-3 mb-4 text-left"
                style={{
                  background: C.amberDim,
                  border: `1px solid rgba(255,182,39,0.3)`,
                  borderRadius: 3,
                }}
                aria-label={`Review ${unregisteredOriginsCount} unregistered-origin records on ${site.domain}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={12} color={C.amberFg} />
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.amberFg,
                      letterSpacing: 1,
                      fontWeight: 600,
                    }}
                  >
                    UNREGISTERED ORIGINS
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    lineHeight: 1.4,
                  }}
                >
                  Sill recorded {unregisteredOriginsCount}{' '}
                  {unregisteredOriginsCount === 1 ? 'record' : 'records'}{' '}
                  from origins that aren&apos;t registered for this site.
                  <span style={{ color: C.muted }}> Review →</span>
                </div>
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClick}
              className="w-full text-left"
              aria-label={`Open ${site.domain} dashboard`}
            >
              <div className="grid grid-cols-3 gap-2 mb-4">
                <SiteStat label="Calls" value={site.stats?.calls ?? 0} />
                <SiteStat label="Agents" value={site.stats?.agents ?? '—'} />
                <SiteStat label="Records" value={site.stats?.records ?? 0} />
              </div>
              <div
                className="flex items-center gap-2 pt-3"
                style={{ borderTop: `1px solid ${C.borderSoft}` }}
              >
                <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, flex: 1 }}>
                  {/* Founder 2026-10-21: one footer per mode — ready,
                      rail connected but not ready, or plain Discovery. */}
                  {siteMode(site) === 'transactions_ready'
                    ? 'Agents can place orders'
                    : (site.connected_rails?.length ?? 0) > 0
                      ? siteModeDetailLabel(site)
                      : 'Discovery mode · no transactional path yet'}
                </span>
                <ChevronRight size={14} color={C.muted} />
              </div>
            </button>
            {staleSignal !== null ? (
              <div
                className="p-4 mt-4"
                style={{
                  background: C.coralDim,
                  border: `1px solid rgba(255,107,71,0.3)`,
                  borderRadius: 3,
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={12} color={C.coralFg} />
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.coralFg,
                      letterSpacing: 1,
                      fontWeight: 600,
                    }}
                  >
                    NO RECENT BEACONS
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    lineHeight: 1.5,
                    marginBottom: 8,
                  }}
                >
                  No beacons in {staleSignal.age_label}. The site key may
                  have rotated — re-check your install snippet against the
                  current key in Settings.
                </div>
                <button
                  type="button"
                  onClick={onInstall}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.limeFg,
                    fontWeight: 500,
                    textDecoration: 'underline',
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                  }}
                >
                  View install snippet →
                </button>
              </div>
            ) : null}
            {/* ADR-0181 §1 — Shopify Storefront re-authorize / retry
               affordance. Visible only when the site's connector-status
               fetch returned a `storefront_consent_status` (i.e. it IS
               a Shopify site) that is NOT 'granted'. Copy is precise +
               low-marketing per CLAUDE.md §8 — names the outcome
               ("recommendation lookups by SKU"), not the mechanism. The
               'pending_retry' variant renders a "Retry" CTA calling
               the mint endpoint; every other non-granted variant renders
               a "Re-authorize Shopify" link that reuses the existing
               OAuth install path via `onInstall`. */}
            {shopifyStorefrontConsentStatus !== undefined
              && shopifyStorefrontConsentStatus !== 'granted' ? (
              <div
                className="p-4 mt-4"
                style={{
                  background: C.amberDim,
                  border: `1px solid rgba(255,182,39,0.3)`,
                  borderRadius: 3,
                }}
                data-testid="shopify-storefront-consent-band"
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={12} color={C.amberFg} />
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.amberFg,
                      letterSpacing: 1,
                      fontWeight: 600,
                    }}
                  >
                    RECOMMENDATIONS BY SKU
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    lineHeight: 1.5,
                    marginBottom: 8,
                  }}
                >
                  {shopifyStorefrontConsentStatus === 'pending_retry'
                    ? (
                      'Storefront access is set up but the last mint did'
                      + ' not complete. Retry to enable recommendation'
                      + ' lookups by SKU.'
                    )
                    : (
                      'Re-authorize Shopify to enable recommendation'
                      + ' lookups by SKU.'
                    )}
                </div>
                {shopifyStorefrontConsentStatus === 'pending_retry' ? (
                  <button
                    type="button"
                    onClick={(): void => {
                      void handleRetryStorefrontMint();
                    }}
                    disabled={retryingStorefrontMint}
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.limeFg,
                      fontWeight: 500,
                      textDecoration: 'underline',
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      cursor: retryingStorefrontMint ? 'wait' : 'pointer',
                    }}
                    data-testid="shopify-storefront-retry-cta"
                  >
                    {retryingStorefrontMint
                      ? 'Retrying…'
                      : 'Retry →'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onInstall}
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.limeFg,
                      fontWeight: 500,
                      textDecoration: 'underline',
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                    }}
                    data-testid="shopify-storefront-reauthorize-cta"
                  >
                    Re-authorize Shopify →
                  </button>
                )}
              </div>
            ) : null}
          </>
        ) : state === 'pending_verification' ? (
          <div
            className="p-4"
            style={{
              background: C.amberDim,
              border: `1px solid rgba(255,182,39,0.3)`,
              borderRadius: 3,
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={12} color={C.amberFg} />
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.amberFg,
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                NOT YET VERIFIED
              </span>
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
                marginBottom: 8,
              }}
            >
              Install the embed and verify ownership of {site.domain} to
              finish setup. Connecting Shopify proves ownership in one
              step.
            </div>
            <button
              type="button"
              onClick={onInstall}
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.limeFg,
                fontWeight: 500,
                textDecoration: 'underline',
                background: 'transparent',
                border: 0,
                padding: 0,
              }}
            >
              Continue setup →
            </button>
          </div>
        ) : state === 'awaiting_first_ping_shopify_autoinstall' ? (
          // ADR-0053 §3.3 — Shopify-OAuth-verified site whose ScriptTag
          // auto-install succeeded. The merchant has NOT pasted (and
          // need not paste) any snippet. Card surfaces "recording on
          // first agent visit"; no "view install snippet" CTA.
          <div
            className="p-4"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                AWAITING FIRST AGENT VISIT
              </span>
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
              }}
            >
              Sill installed the embed on your Shopify storefront.
              Recording starts on the first agent visit.
            </div>
          </div>
        ) : shopifyInstallStatus === 'scope_denied'
          || shopifyInstallStatus === 'pending'
          || shopifyInstallStatus === 'pending_retry' ? (
          // ADR-0056 — every non-installed terminal of the Shopify
          // ScriptTag auto-install (`scope_denied`, `pending_retry`,
          // `pending`) falls through to the SAME manual-install
          // surface. The previous branches looped indefinitely on
          // "Reconnect Shopify" / "Retry install" — both buttons
          // re-ran an auto-install path that is structurally blocked
          // (OS-2.0 themes do not honor ScriptTag injections) AND
          // that the founder-side OAuth grant deterministically drops
          // `write_script_tags` from. The merchant now sees the
          // 5-step manual install with the hash-form snippet; the
          // OAuth-retry survives as a small secondary link.
          <ShopifyManualInstallPanel
            site_key_hash={site.site_key_hash}
            onCheckInstalled={handleCheckInstalled}
            checking={checkingPing}
            checkResultMessage={checkResultMessage}
            showRetryAutoInstall
            onRetryAutoInstall={handleRetryInstall}
          />
        ) : (
          // Default `awaiting_install` — non-Shopify sites OR Shopify
          // sites the connector-status fetch hasn't returned for yet.
          // Preserves the existing paste-the-snippet copy verbatim.
          <div
            className="p-4"
            style={{
              background: C.amberDim,
              border: `1px solid rgba(255,182,39,0.3)`,
              borderRadius: 3,
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={12} color={C.amberFg} />
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.amberFg,
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                AWAITING FIRST PING
              </span>
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
                marginBottom: 8,
              }}
            >
              Verified. Agent traffic will appear here once an AI agent
              visits your site.
            </div>
            <button
              type="button"
              onClick={onInstall}
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.limeFg,
                fontWeight: 500,
                textDecoration: 'underline',
                background: 'transparent',
                border: 0,
                padding: 0,
              }}
            >
              View setup details →
            </button>
          </div>
        )}
      </div>
    </Card>
    {/* Stage 1 (ADR-0051 §5) — intent picker. Two side-by-side cards.
        "Stop recording" routes into the corrected stage-2 confirm; "Erase
        content" is rendered disabled with the explicit "available in a
        later release" label per ADR-0051 §2.1. The disabled affordance
        is the honest surface: the merchant sees what's coming, knows it
        is not here yet, and can pick "Stop recording" with full
        understanding of what it does and does not do. */}
    <Modal
      open={intentOpen}
      onClose={() => setIntentOpen(false)}
      size="lg"
      title="Remove this site?"
      subtitle="Pick the outcome that matches what you want."
      closeOnBackdrop={false}
    >
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Stop recording — Slice 1, enabled. */}
          <div
            className="p-5 flex flex-col"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              STOP RECORDING
            </div>
            <div
              style={{
                fontFamily: serifFont,
                fontSize: 22,
                color: C.text,
                letterSpacing: -0.5,
                lineHeight: 1.1,
                marginBottom: 10,
              }}
            >
              Stop recording
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                lineHeight: 1.55,
                marginBottom: 16,
                flex: 1,
              }}
            >
              Stop new agent traffic, remove from your active sites, KEEP
              the audit records on Sill&apos;s servers. Those records are
              not deleted — contact support if you need to export them.
              Reversible &mdash; you can re-add this domain at any time
              as a new site (new site_id, new chain).
            </div>
            <button
              type="button"
              onClick={() => {
                setIntentOpen(false);
                setConfirmOpen(true);
              }}
              className="px-4 py-2.5"
              style={{
                background: C.coral,
                color: C.ink,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 2,
                border: 0,
                cursor: 'pointer',
              }}
            >
              Choose stop recording
            </button>
          </div>
          {/* Erase content — Slice 2, rendered DISABLED in Slice 1 with the
              explicit "available in a later release" label. No erasure
              logic is wired; the button is non-interactive. */}
          <div
            className="p-5 flex flex-col"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              opacity: 0.6,
            }}
            aria-disabled="true"
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              AVAILABLE IN A LATER RELEASE
            </div>
            <div
              style={{
                fontFamily: serifFont,
                fontSize: 22,
                color: C.text,
                letterSpacing: -0.5,
                lineHeight: 1.1,
                marginBottom: 10,
              }}
            >
              Erase content
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                lineHeight: 1.55,
                marginBottom: 16,
                flex: 1,
              }}
            >
              Permanently destroy this site&apos;s audit content. Sill
              destroys the cryptographic key used to encrypt the records;
              the ciphertext stays in our database but cannot be
              decrypted. 7-day cool-down before the key is destroyed; you
              can cancel during the cool-down. Not reversible after the
              cool-down. Owner role only.
            </div>
            <button
              type="button"
              disabled
              aria-disabled="true"
              className="px-4 py-2.5"
              style={{
                background: 'transparent',
                color: C.muted,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 2,
                border: `1px solid ${C.border}`,
                cursor: 'not-allowed',
              }}
            >
              Choose erase content
            </button>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={() => setIntentOpen(false)}
            className="px-4 py-2.5"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.muted,
              fontFamily: sansFont,
              fontSize: 13,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
    {/* Stage 2 (ADR-0051 §2.1) — corrected soft-delete confirmation.
        The body + consequences are MECHANICALLY TRUE: the audit records
        stay on Sill's servers and are reachable from the "Show deleted
        sites" toggle in the Audit view (Slice 1 ships that path). No
        marketing language; no compliance claims. */}
    <ConfirmDialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      title="Remove this site?"
      body={
        <>
          {site.domain} will stop recording and be removed from your
          sites. Past audit records are preserved on Sill&apos;s servers;
          contact support if you need to export them. Records are not
          erased &mdash; this is a stop-recording control, not a content
          purge. If you need the records gone from Sill, choose Erase
          instead (available in a later release).
        </>
      }
      confirmLabel={deleting ? 'Removing…' : 'Remove site'}
      confirmTone="coral"
      icon={Trash2}
      onConfirm={() => {
        void handleConfirmDelete();
      }}
      consequences={[
        'The embed snippet on this domain stops being recognized.',
        'The site disappears from your active Sites list.',
        'Past audit records remain stored on Sill’s servers; contact support if you need to export them.',
        `This action is reversible — you can re-add ${site.domain} as a new site (new site_id, new chain) at any time.`,
      ]}
    />
    </>
  );
}
